import React, { useEffect, useRef, useState } from 'react';
import { Loader2, LogIn, LogOut, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { InAppCamera } from '@/components/transport/InAppCamera';
import { fileToDataUrl } from '@/services/transportApi';
import { compressImageFile } from '@/utils/imageCompression';

/** Compulsory front-camera selfie before every attendance punch. onConfirm(dataUrl). */
export const SelfiePunchDialog = ({ kind, busy, onConfirm, onClose }) => {
  const inputRef = useRef(null);
  const [shot, setShot] = useState(null);
  useEffect(() => { setShot(null); }, [kind]);
  const isOut = kind === 'out';

  const pickFile = () => inputRef.current?.click();
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    let x = f;
    try { x = await compressImageFile(f, { maxEdge: 720, quality: 0.7, sizeThresholdBytes: 150 * 1024 }); } catch { /* keep original */ }
    setShot(await fileToDataUrl(x));
  };

  return (
    <Dialog open={!!kind} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent data-testid="selfie-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{isOut ? 'Check Out' : 'Check In'} — take a selfie</DialogTitle>
          <DialogDescription className="sr-only">Selfie for attendance</DialogDescription>
        </DialogHeader>
        <input ref={inputRef} type="file" accept="image/*" capture="user" className="hidden" onChange={onFile} data-testid="selfie-input" />
        {shot ? (
          <img src={shot} alt="selfie" className="w-full max-h-[50vh] object-contain rounded-lg border bg-gray-50" data-testid="selfie-preview" />
        ) : (
          <>
            <InAppCamera facing="user" onShot={setShot} onCancel={onClose} onFallback={pickFile} maxEdge={720} testId="selfie-camera" />
            <button type="button" onClick={pickFile} className="text-xs text-gray-500 underline underline-offset-2" data-testid="selfie-fallback">Use phone camera</button>
          </>
        )}
        <p className="text-xs text-gray-500">A selfie is required for every Check In and Check Out — face the camera, then tap Capture.</p>
        <DialogFooter className="gap-2">
          {shot && <Button variant="outline" onClick={() => setShot(null)} disabled={busy} data-testid="selfie-retake"><RefreshCw className="w-4 h-4 mr-1" />Retake</Button>}
          <Button onClick={() => onConfirm(shot)} disabled={!shot || busy} className={isOut ? 'bg-slate-700 hover:bg-slate-800' : 'bg-emerald-600 hover:bg-emerald-700'} data-testid="selfie-confirm">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : isOut ? <LogOut className="w-4 h-4 mr-1" /> : <LogIn className="w-4 h-4 mr-1" />}
            {isOut ? 'Check Out' : 'Check In'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SelfiePunchDialog;
